import { useState } from 'react';
import { api, type MessageRow } from '../lib/api';

interface Props {
  proposal: MessageRow;
  decision?: MessageRow;
  sessionId: string;
  onDecided: () => void | Promise<void>;
}

/**
 * AuthorizeCard.
 *
 * Renders inline in the conversation when an authorize_proposal row is
 * present. Per Constitution Article 1.5, only the human authorizes a write.
 * The agent emits a tool_use, the harness brokers it as a proposal, and
 * Mark accepts or discards here.
 *
 * If a matching authorize_decision exists, the card collapses to a compact
 * receipt showing what was decided.
 */
export function AuthorizeCard({ proposal, decision, sessionId, onDecided }: Props) {
  const [pending, setPending] = useState<null | 'accept' | 'discard'>(null);
  const [error, setError] = useState<string | null>(null);

  const meta = safeMeta(proposal.metadata);
  const toolName = (meta.tool_name as string) ?? 'unknown';
  const toolInput = (meta.tool_input as Record<string, unknown> | undefined) ?? {};
  const summary = (meta.summary as string) ?? proposal.content ?? toolName;

  if (decision) {
    const dmeta = safeMeta(decision.metadata);
    const decisionKind = (dmeta.decision as string) ?? 'decided';
    const err = (dmeta.error as string | null | undefined) ?? null;
    return (
      <div
        className="self-start max-w-[75%] md:max-w-[600px] rounded-md px-3 py-2"
        style={{
          background: 'var(--agent-bg-soft)',
          borderLeft: '2px solid var(--agent-edge-soft)',
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="font-mono text-[10px] uppercase tracking-mono-wide"
            style={{ color: 'var(--agent-fg-soft)' }}
          >
            {decisionKind === 'accept'
              ? err
                ? 'authorized · failed'
                : 'authorized · written'
              : 'discarded'}
          </span>
          <span
            className="font-mono text-[10px]"
            style={{ color: 'var(--agent-fg-soft)' }}
          >
            {summary}
          </span>
        </div>
        {err && <div className="mt-1 text-xs text-paper-300 font-mono">{err}</div>}
      </div>
    );
  }

  async function decide(d: 'accept' | 'discard') {
    setPending(d);
    setError(null);
    try {
      await api.authorize(sessionId, proposal.id, d);
      await onDecided();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'authorization failed');
    } finally {
      setPending(null);
    }
  }

  return (
    <div
      className="self-start max-w-[75%] md:max-w-[600px] rounded-lg overflow-hidden border"
      style={{
        background: 'var(--agent-card-bg)',
        borderColor: 'var(--agent-card-edge)',
      }}
    >
      <div className="px-4 pt-3 pb-2">
        <div
          className="font-mono text-[10px] uppercase tracking-mono-wide"
          style={{ color: 'var(--agent-subtle)' }}
        >
          proposal · {toolName}
        </div>
        <div
          className="mt-1 text-[14px] font-semibold"
          style={{ color: 'var(--agent-fg-body)' }}
        >
          {summary}
        </div>
        <ProposalDetails toolName={toolName} input={toolInput} />
        <div
          className="mt-2 text-[11px] leading-snug"
          style={{ color: 'var(--agent-fg-soft)' }}
        >
          The agent proposed this write. The platform queued it. The write
          executes only when you accept.
        </div>
        {error && (
          <div className="mt-2 text-xs text-paper-300" role="alert">
            {error}
          </div>
        )}
      </div>
      <div
        className="flex items-stretch border-t"
        style={{ borderColor: 'var(--agent-card-edge)' }}
      >
        <button
          type="button"
          onClick={() => decide('discard')}
          disabled={pending !== null}
          className="simpliloquy-button-focus flex-1 h-11 text-[13px] font-medium hover:bg-night-800
                     disabled:opacity-50 transition-colors"
          style={{ color: 'var(--agent-fg-soft)' }}
        >
          {pending === 'discard' ? 'Discarding…' : 'Discard'}
        </button>
        <span aria-hidden="true" className="w-px" style={{ background: 'var(--agent-card-edge)' }} />
        <button
          type="button"
          onClick={() => decide('accept')}
          disabled={pending !== null}
          className="simpliloquy-button-focus flex-1 h-11 text-[13px] font-semibold
                     disabled:opacity-50 transition-colors
                     hover:brightness-110"
          style={{
            background: 'var(--agent-accent)',
            color: 'var(--agent-fg-on-accent)',
          }}
        >
          {pending === 'accept' ? 'Authorizing…' : 'Authorize'}
        </button>
      </div>
    </div>
  );
}

function ProposalDetails({
  toolName,
  input,
}: {
  toolName: string;
  input: Record<string, unknown>;
}) {
  if (toolName === 'log_entry') {
    const kind = (input.kind as string) ?? '';
    const title = (input.title as string) ?? '';
    const body = (input.body as string) ?? '';
    return (
      <div
        className="mt-2 rounded-md p-2 text-[12px] font-mono leading-relaxed"
        style={{ background: 'var(--agent-bg)', color: 'var(--agent-fg-body)' }}
      >
        <div>
          <span style={{ color: 'var(--agent-subtle)' }}>kind</span> {kind}
        </div>
        <div className="mt-1">
          <span style={{ color: 'var(--agent-subtle)' }}>title</span> {title}
        </div>
        {body && (
          <div
            className="mt-1 whitespace-pre-wrap line-clamp-6"
            style={{ color: 'var(--agent-fg-body)' }}
          >
            {body.length > 480 ? body.slice(0, 480) + '…' : body}
          </div>
        )}
      </div>
    );
  }
  // Generic fallback: show the input as JSON.
  return (
    <pre
      className="mt-2 rounded-md p-2 text-[11px] font-mono whitespace-pre-wrap"
      style={{ background: 'var(--agent-bg)', color: 'var(--agent-fg-soft)' }}
    >
      {JSON.stringify(input, null, 2)}
    </pre>
  );
}

function safeMeta(metadata: string | null): Record<string, unknown> {
  if (!metadata) return {};
  try {
    return JSON.parse(metadata) as Record<string, unknown>;
  } catch {
    return {};
  }
}
